import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { optimizeImageUrl } from '../lib/optimizeImage';

/**
 * Photography lightbox/gallery modal.
 * Props:
 *   project: { title, description, gallery: string[], imageUrl }
 *   onClose: () => void
 */
const PhotoGallery = ({ project, onClose }) => {
  const photos = [
    ...(Array.isArray(project.gallery) ? project.gallery : []),
  ];
  // Make sure the cover image is also browsable.
  if (project.imageUrl && !photos.includes(project.imageUrl)) {
    photos.unshift(project.imageUrl);
  }
  const total = photos.length;
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [next, prev, onClose]);

  if (total === 0) return null;
  const active = photos[index];

  return (
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex flex-col"
      data-testid="photo-gallery"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-gray-800 shrink-0">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-semibold text-white truncate">{project.title}</h2>
          <p className="text-xs sm:text-sm text-gray-400 truncate">{project.description}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-gray-400 font-mono tabular-nums" data-testid="gallery-counter">
            {index + 1} / {total}
          </span>
          <button
            onClick={onClose}
            aria-label="Close gallery"
            data-testid="gallery-close"
            className="w-10 h-10 rounded-full bg-gray-800/80 hover:bg-gray-700 text-white flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Active image */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <button
          onClick={prev}
          aria-label="Previous photo"
          data-testid="gallery-prev"
          className="absolute left-4 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-blue-600 text-white flex items-center justify-center transition-colors"
        >
          <ChevronLeft size={28} />
        </button>
        <img
          src={optimizeImageUrl(active, 1600)}
          alt={`${project.title} ${index + 1} of ${total}`}
          data-testid="gallery-active-image"
          loading="lazy"
          decoding="async"
          className="max-w-full max-h-full object-contain px-4 sm:px-20 select-none"
          draggable={false}
        />
        <button
          onClick={next}
          aria-label="Next photo"
          data-testid="gallery-next"
          className="absolute right-4 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-blue-600 text-white flex items-center justify-center transition-colors"
        >
          <ChevronRight size={28} />
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="border-t border-gray-800 px-4 sm:px-8 py-3 shrink-0 overflow-x-auto" data-testid="gallery-thumbs">
        <div className="flex gap-2 w-max">
          {photos.map((url, i) => (
            <button
              key={`${url}-${i}`}
              onClick={() => setIndex(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Show photo ${i + 1}`}
              className={`relative h-16 w-24 rounded-md overflow-hidden border-2 transition-all flex-shrink-0 ${
                i === index ? 'border-blue-400' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={optimizeImageUrl(url, 400)} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhotoGallery;
